import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {reportsApi} from '../api/reports.api';
import {
  createReportBody,
  getReportFields,
  readReportColumns,
  validateReportForm,
} from '../helpers/report-builder.helpers';
import {ReportField, ReportFormErrors, ReportFormValues} from '../types/report-builder.types';
import {useReportPermissions} from './use-report-permissions';
import {useReportError} from './use-report-error';

const emptyForm = (): ReportFormValues => ({name: '', comment: '', isPublic: false, columns: []});

export const useReportEditor = (id?: string) => {
  const navigate = useNavigate();
  const getError = useReportError();
  const {canView, canManage, companyId} = useReportPermissions();
  const [values, setValues] = useState<ReportFormValues>(emptyForm);
  const [initialValues, setInitialValues] = useState('');
  const [fields, setFields] = useState<ReportField[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [accessError, setAccessError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [errors, setErrors] = useState<ReportFormErrors>({columnErrors: {}});
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const savingRef = useRef(false);
  const allowNavigation = useRef(false);
  const mounted = useRef(true);
  const isEdit = id !== undefined;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setAccessError('');
    setInitialValues('');
    allowNavigation.current = false;
    if (!canView || !canManage) {
      setLoading(false);
      setAccessError(
        !canView
          ? 'Для открытия конструктора нужны права просмотра отчётов.'
          : 'Для создания и редактирования отчётов нужны права менеджера.',
      );
      return;
    }
    if (isEdit && (!/^\d+$/.test(id || '') || !Number.isSafeInteger(Number(id)))) {
      setLoading(false);
      setAccessError('Отчёт не найден или недоступен.');
      return;
    }
    const load = async () => {
      try {
        const [schema, availableReports, myReports] = await Promise.all([
          reportsApi.getFields(),
          isEdit ? reportsApi.list({id: Number(id), myOnly: false}) : Promise.resolve([]),
          isEdit ? reportsApi.list({myOnly: true}) : Promise.resolve([]),
        ]);
        if (cancelled) return;
        const report = availableReports.find((item) => item.id === Number(id));
        if (isEdit && !report) {
          setAccessError('Отчёт не найден или недоступен.');
          return;
        }
        if (report && !myReports.some((item) => item.id === report.id)) {
          setAccessError(
            'Редактировать можно только свои отчёты. Этот отчёт доступен для формирования в конструкторе.',
          );
          return;
        }
        const nextFields = getReportFields(schema);
        if (!nextFields.length) throw new Error('Нет доступных полей отчёта. Попробуйте загрузить их ещё раз.');
        const nextValues = report
          ? {
              name: report.name,
              comment: report.comment || '',
              isPublic: report.isPublic,
              columns: readReportColumns(report.body, nextFields),
            }
          : emptyForm();
        setFields(nextFields);
        setValues(nextValues);
        setInitialValues(JSON.stringify(nextValues));
        setErrors({columnErrors: {}});
        setSaveError('');
      } catch (error) {
        if (cancelled) return;
        const details = await getError(error, 'Не удалось загрузить редактор отчёта.');
        if (!cancelled) setLoadError(details.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, canView, canManage, companyId, reloadKey, getError]);

  const updateValues = (nextValues: ReportFormValues) => {
    setValues(nextValues);
    setSaveError('');
    setErrors({columnErrors: {}});
  };

  const save = async () => {
    if (savingRef.current || loading || accessError || loadError || !canManage || !canView) return;
    const nextErrors = validateReportForm(values, fields);
    setErrors(nextErrors);
    setSaveError('');
    if (
      nextErrors.name ||
      nextErrors.comment ||
      nextErrors.columns ||
      nextErrors.body ||
      Object.keys(nextErrors.columnErrors).length
    )
      return;
    savingRef.current = true;
    setSaving(true);
    try {
      await reportsApi.save({
        ...(isEdit ? {id: Number(id)} : {}),
        name: values.name.trim(),
        comment: values.comment.trim() || null,
        isPublic: values.isPublic,
        body: createReportBody(values.columns, fields),
        ...(companyId ? {companyId} : {}),
      });
      if (!mounted.current) return;
      allowNavigation.current = true;
      setInitialValues(JSON.stringify(values));
      navigate('/reports/builder', {replace: true, state: {reportSaved: true}});
    } catch (error) {
      const details = await getError(error, 'Не удалось сохранить отчёт. Попробуйте ещё раз.');
      if (!mounted.current) return;
      if (details.code === '4039') setErrors({name: details.message, columnErrors: {}});
      else setSaveError(details.message);
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  return {
    values,
    updateValues,
    fields,
    loading,
    loadError,
    accessError,
    saveError,
    errors,
    saving,
    save,
    isEdit,
    allowNavigation,
    dirty: Boolean(initialValues) && JSON.stringify(values) !== initialValues,
    retry: () => setReloadKey((value) => value + 1),
  };
};
